import "./LanguageSelector.scss";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconArrowPoint } from "../../../../../common/icons/IconArrowPoint/IconArrowPoint";
import { localeCodes } from "../../../../../common/constants/supportedLocales";
import { dispatchLanguageChangedEvent } from "../../../../../common/util/dispatchEvent";
import { persistor, useAppSelector } from "../../../../../store";
import defaultContent from "../../../../../content/profileSelectorDropdown";

const TEST_LOCALE_CODES = new Set(["bi-HT", "ln-HT"]);

const getNativeLocaleName = (code: string, fallback: string): string => {
  try {
    const nativeName = new Intl.DisplayNames(code, { type: "language" }).of(code);
    if (!nativeName || nativeName.toLowerCase() === code.toLowerCase()) return fallback;
    return nativeName;
  } catch {
    return fallback;
  }
};

interface LanguageSelectorProps {
  onBack: () => void;
  activeLocale?: string;
}

export const LanguageSelector = ({ onBack, activeLocale }: LanguageSelectorProps) => {
  const { t } = useTranslation(["profileSelectorDropdown"]);
  const preferredLocales = useAppSelector((state) => state.boot.configuration.preferredLocales);

  const displayLocales = useMemo(() => {
    const isLocalhost = window.location.origin.includes("localhost");
    const base = localeCodes.filter((locale) => isLocalhost || !TEST_LOCALE_CODES.has(locale.code));
    if (!preferredLocales?.length) return base;
    const preferred = new Set(preferredLocales);
    return base.filter((locale) => preferred.has(locale.code));
  }, [preferredLocales]);

  const nativeLocaleNames = useMemo(
    () => new Map(displayLocales.map((locale) => [locale.code, getNativeLocaleName(locale.code, locale.locale)])),
    [displayLocales],
  );

  const handleSelectLocale = async (code: string) => {
    dispatchLanguageChangedEvent(code);
    await persistor.flush();
    window.location.reload();
  };

  return (
    <div className="LanguageSelector" data-testid="language-selector">
      <div className="LanguageSelector__header">
        <button
          type="button"
          className="LanguageSelector__back"
          onClick={onBack}
          aria-label={t("back", defaultContent.back)}
        >
          <IconArrowPoint />
        </button>
        <span className="LanguageSelector__title">{t("selectLanguage", defaultContent.selectLanguage)}</span>
      </div>

      <div className="LanguageSelector__list" role="list">
        {displayLocales.map((locale) => {
          const isActive = locale.code === activeLocale;
          return (
            <button
              key={locale.code}
              type="button"
              className={`LanguageSelector__item${isActive ? " LanguageSelector__item--active" : ""}`}
              aria-current={isActive ? "true" : undefined}
              onClick={() => handleSelectLocale(locale.code)}
            >
              {nativeLocaleNames.get(locale.code)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
